import { stringifyAnything } from "@nvon/baseline";
import { useEffect, useRef } from "react";
/**
 * Use this hook to check which dependencies change between renders.
 * Performs a shallow `Object.is` check, as react does.
 *
 * @example
 * ```
 * useTrackChanges("myBuggyComponent", {
 *   somePropterty,
 *   anotherProp: that.comes.fromAnObject
 * })
 * ```
 */
export function useTrackChanges(componentName, dependenciesToTrack, logRenders = true) {
    const dependencyHistory = useRef([]).current;
    const name = useRef(componentName);
    const isFirstRender = dependencyHistory.length === 0;
    const lastRenderState = isFirstRender
        ? undefined
        : dependencyHistory[dependencyHistory.length - 1];
    dependencyHistory.push(dependenciesToTrack);
    useEffect(() => {
        if (!logRenders) {
            return;
        }
        const theName = name.current;
        console.log(`${theName} has been mounted!!`);
        return () => {
            console.log(`${theName} has unmounted!!`);
        };
    }, [logRenders]);
    useEffect(() => {
        if (!logRenders) {
            return;
        }
        console.log(`💻 ${componentName} is rendering ${isFirstRender ? "for the first time." : "again."}.`);
    });
    useEffect(() => {
        Object.entries(dependenciesToTrack).forEach(([key, value]) => {
            const hasChanged = !lastRenderState || !Object.is(lastRenderState[key], value);
            if (hasChanged) {
                console.log(`↪️ "${key}" changed! New value:`, stringifyAnything(value));
            }
        });
    }, [dependenciesToTrack, lastRenderState]);
}
/**
 * Tracks a set of variables. Quick alternative to useTrackChanges,
 *  if you only care about vars when they change and don't need a name.
 */
export const useTrackVariables = (vars) => useTrackChanges("vars", vars, false);
//# sourceMappingURL=useTrackChanges.js.map